// OurBestSellers.js

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShoppingCart,
  ChefHat,
  Utensils,
  X,
  Check,
  Minus,
  Plus,
} from 'lucide-react';
import { useDispatch } from 'react-redux';
import { addToCart } from '../store/slices/cartSlice';
import { toast } from 'react-toastify';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase'; // Your Firestore instance

const OurBestSellers = () => {
  const dispatch = useDispatch();

  // Local state to track selections, modals, chef note, and Firestore items
  const [selections, setSelections] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Renamed states for chef note to match Noodles component functionality
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [chefComment, setChefComment] = useState('');
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [items, setItems] = useState([]); // Bestsellers from Firestore

  // Calculate price based on size, variants, and extras
  const calculatePrice = (item, itemSelections = {}) => {
    let price = item.price; // default price (e.g. Large for pizza)
    if (item.extras.sizes && itemSelections.size) {
      const sizeOption = item.extras.sizes.find(
        (s) => s.name === itemSelections.size
      );
      if (sizeOption) {
        price = sizeOption.price;
      }
    }
    // Process variants (if any)
    if (item.extras.variants && itemSelections.variants) {
      Object.entries(itemSelections.variants).forEach(([category, selectedOption]) => {
        const variantOption = item.extras.variants[category].find(
          (option) => option.name === selectedOption
        );
        if (variantOption) {
          price += variantOption.priceModifier;
        }
      });
    }
    // Add topping costs if selected
    if (itemSelections.extras?.toppings) {
      price += itemSelections.extras.toppings.reduce(
        (acc, topping) => acc + topping.price * (topping.quantity || 1),
        0
      );
    }
    return price;
  };

  // Listen for real-time updates from Firestore
  useEffect(() => {
    const bestsellerRef = doc(db, 'menuCategories', 'Bestseller');
    const unsubscribe = onSnapshot(bestsellerRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        setItems(docSnapshot.data().items);
      }
    });
    return () => unsubscribe();
  }, []);

  // Handlers for UI interactions
  const handleVariantChange = (itemId, variantCategory, value) => {
    setSelections((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        variants: {
          ...prev[itemId]?.variants,
          [variantCategory]: value,
        },
      },
    }));
  };

  const handleOpenModal = (itemId) => {
    const item = items.find((item) => item.id === itemId);
    setSelectedItem(item);
    setSelectedItemId(itemId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedItemId(null);
    setSelectedItem(null);
  };

  const handleSizeChange = (itemId, value) => {
    setSelections((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], size: value },
    }));
  };

  const handleToppingChange = (itemId, topping, isChecked) => {
    setSelections((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        extras: {
          ...prev[itemId]?.extras || {},
          toppings: isChecked
            ? [...(prev[itemId]?.extras?.toppings || []), { ...topping, quantity: 1 }]
            : (prev[itemId]?.extras?.toppings || []).filter((t) => t.name !== topping.name),
        },
      },
    }));
  };

  const handleToppingQuantityChange = (itemId, toppingName, value) => {
    const toppingInfo = selectedItem.extras.toppings.find((t) => t.name === toppingName);
    setSelections((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        extras: {
          ...prev[itemId]?.extras || {},
          toppings: (prev[itemId]?.extras?.toppings || []).map((topping) =>
            topping.name === toppingName
              ? {
                  ...topping,
                  quantity: Math.max(1, parseInt(value)),
                  price: toppingInfo.price,
                }
              : topping
          ),
        },
      },
    }));
  };

  const handleQuantityChange = (itemId, delta) => {
    setSelections((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        quantity: Math.max(1, (prev[itemId]?.quantity || 1) + delta),
      },
    }));
  };

  // Updated handleAddToCart to include selected pizza size and chef comment in the name
  const handleAddToCart = (item) => {
    const itemSelections = selections[item.id] || {};
    let totalPrice = calculatePrice(item, itemSelections);

    // Append variant details if available
    let variantText = '';
    if (item.extras.variants && itemSelections.variants) {
      Object.entries(item.extras.variants).forEach(([category, options]) => {
        const selectedOptionName = itemSelections.variants[category];
        const option = options.find((opt) => opt.name === selectedOptionName);
        if (option) {
          variantText += ` - ${option.name}`;
        }
      });
    }

    // Append the selected size to the item name
    let sizeText = '';
    if (item.extras.sizes && itemSelections.size) {
      sizeText = ` (${itemSelections.size})`;
    }

    // Create the cart item with updated name and include chef comment
    const cartItem = {
      id: `${item.id}-${Date.now()}`,
      name: item.name + sizeText + variantText,
      price: totalPrice,
      quantity: itemSelections.quantity || 1,
      extras: itemSelections.extras || {},
      variants: itemSelections.variants || {},
      chefComment: chefComment, // Include chef comment from modal
    };

    dispatch(addToCart(cartItem));
    toast.success(`${item.name} added to cart!`, {
      position: 'top-right',
      autoClose: 3000,
    });
    // Clear the chef comment after adding to cart
    setChefComment('');
  };

  // Animation variants for framer-motion
  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -50 },
  };

  const renderVariantSelectors = (item) => {
    if (!item.extras.variants) return null;
    return (
      <div className="space-y-2">
        {Object.entries(item.extras.variants).map(([variantCategory, options]) => (
          <div key={variantCategory} className="relative">
            <select
              onChange={(e) =>
                handleVariantChange(item.id, variantCategory, e.target.value)
              }
              className="w-full appearance-none bg-black border border-gray-700 text-gray-300 py-2 px-3 pr-8 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              value={
                (selections[item.id]?.variants &&
                  selections[item.id].variants[variantCategory]) ||
                ''
              }
            >
              {/* Provide clear names for variants */}
              <option value="">
                {variantCategory === 'yamType'
                  ? 'Select Yam Type'
                  : variantCategory === 'eggOption'
                  ? 'Select Egg Option'
                  : `Select ${variantCategory}`}
              </option>
              {options.map((option, idx) => (
                <option key={idx} value={option.name}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
    );
  };

  const renderMenuItem = (item) => {
    const itemSelections = selections[item.id] || {};
    const quantity = itemSelections.quantity || 1;
    const displayPrice = calculatePrice(item, itemSelections);
  
    return (
      <motion.div
        key={item.id}
        className="w-full bg-black rounded-xl overflow-hidden shadow-lg border border-gray-800"
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        transition={{ type: 'spring', stiffness: 300, damping: 17 }}
      >
        <div className="relative">
          <img
            src={item.imageUrl}
            alt={item.name}
            className="w-full h-40 object-cover"
          />
          <div className="absolute top-4 right-4">
            <div className="bg-black bg-opacity-50 px-3 py-1 rounded-full">
              <span className="bg-red-600 text-white font-medium text-base py-1 px-3 rounded-full">
                ₦{displayPrice.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <h3 className="text-red-500 font-bold text-lg">{item.name}</h3>
            <p className="text-gray-400 mt-1 text-xs">{item.description}</p>
          </div>
          {item.extras.sizes && (
            <div className="relative">
              <select
                onChange={(e) => handleSizeChange(item.id, e.target.value)}
                className="w-full text-sm appearance-none bg-black border border-gray-700 text-gray-300 py-2 px-3 pr-8 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                value={itemSelections.size || ''}
              >
                <option value="">Select Size</option>
                {item.extras.sizes.map((size, idx) => (
                  <option key={idx} value={size.name}>
                    {size.name}
                  </option>
                ))}
              </select>
              <ChefHat className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-red-500" />
            </div>
          )}
          {item.extras.variants && renderVariantSelectors(item)}
          <div className="flex gap-2">
            {item.extras.toppings && item.extras.toppings.length > 0 && (
              <button
                onClick={() => handleOpenModal(item.id)}
                className="flex-1 py-1.5 px-2 text-sm bg-red-500 hover:bg-red-600 text-white rounded-lg border border-red-500 flex items-center justify-center gap-1 transition-colors"
              >
                <Utensils className="w-3 h-3" />
                <span>Extras</span>
              </button>
            )}
            {/* Updated Chef Note button to trigger the correct modal */}
            {item.name !== 'Chicken Pie' && item.name !== 'Meat Pie' && (
              <button
                onClick={() => setIsCommentModalOpen(true)}
                className="flex-1 py-1.5 px-2 text-sm bg-red-500 hover:bg-red-600 text-white rounded-lg border border-red-500 flex items-center justify-center gap-1 transition-colors"
              >
                <ChefHat className="w-3 h-3" />
                <span>Chef Note</span>
              </button>
            )}
          </div>
          <div className="flex items-center justify-between pt-2">
            <div className="flex items-center gap-1 bg-black rounded-lg p-1">
              <button
                onClick={() => handleQuantityChange(item.id, -1)}
                className="p-1 text-gray-300 hover:text-red-500 transition-colors"
                disabled={quantity <= 1}
              >
                <Minus className="w-3 h-3" />
              </button>
              <span className="w-6 text-center text-sm font-medium text-red-500">
                {quantity}
              </span>
              <button
                onClick={() => handleQuantityChange(item.id, 1)}
                className="p-1 text-gray-300 hover:text-red-500 transition-colors"
              >
                <Plus className="w-3 h-3" />
              </button>
            </div>
            <button
              onClick={() => handleAddToCart(item)}
              className="flex items-center gap-1 px-4 py-1.5 text-sm bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
            >
              <ShoppingCart className="w-3 h-3" />
              <span>Add to Cart</span>
            </button>
          </div>
        </div>
      </motion.div>
    );
  };
  
  return (
    <section className="relative py-8 bg-black text-red-500 overflow-hidden">
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {items.map((item) => renderMenuItem(item))}
        </div>
      </div>
      <AnimatePresence>
        {isModalOpen && selectedItem && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="bg-black text-red-500 rounded-xl w-96 p-6 border border-gray-700 shadow-lg">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold">
                  Extras {selectedItem.name}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 rounded"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-4">
                {selectedItem.extras.toppings &&
                  selectedItem.extras.toppings.map((topping, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between gap-4"
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={!!(selections[selectedItemId]?.extras?.toppings?.find((t) => t.name === topping.name))} 
                          onChange={(e) => handleToppingChange(selectedItemId, topping, e.target.checked)} 
                          className="w-4 h-4 text-red-500 rounded border-gray-700 focus:ring-red-500 focus:ring-2"
                        />
                        <label className="text-gray-200">
                          {topping.name}
                          <span className="text-sm text-gray-400 block">
                            ₦{topping.price.toLocaleString()}
                          </span>
                        </label>
                      </div>
                      {selections[selectedItemId]?.extras?.toppings?.find((t) => t.name === topping.name) && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() =>
                              handleToppingQuantityChange(
                                selectedItemId,
                                topping.name,
                                Math.max(
                                  1,
                                  (selections[selectedItemId].extras.toppings.find(
                                    (t) => t.name === topping.name
                                  ).quantity || 1) - 1
                                )
                              )
                            }
                            className="p-1 text-gray-300 hover:text-red-500 transition-colors"
                            disabled={
                              selections[selectedItemId].extras.toppings.find(
                                (t) => t.name === topping.name
                              ).quantity <= 1
                            }
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <input
                            type="number"
                            value={
                              selections[selectedItemId]?.extras?.toppings?.find(
                                (t) => t.name === topping.name
                              )?.quantity || 1
                            }
                            min="1"
                            onChange={(e) =>
                              handleToppingQuantityChange(
                                selectedItemId,
                                topping.name,
                                e.target.value
                              )
                            }
                            className="w-16 p-1 bg-gray-800 border border-gray-600 rounded-lg text-center text-gray-200 focus:outline-none focus:ring-2 focus:ring-red-500"
                          />
                          <button
                            onClick={() =>
                              handleToppingQuantityChange(
                                selectedItemId,
                                topping.name,
                                (selections[selectedItemId].extras.toppings.find(
                                  (t) => t.name === topping.name
                                ).quantity || 1) + 1
                              )
                            }
                            className="p-1 text-gray-300 hover:text-red-500 transition-colors"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleCloseModal}
                  className="flex items-center gap-2 px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg border border-red-500 transition-colors focus:ring-2 focus:ring-red-500"
                >
                  <Check className="w-4 h-4" />
                  <span>Done</span>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Updated Chef Note Modal */}
      <AnimatePresence>
        {isCommentModalOpen && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="bg-black text-red-500 rounded-xl w-96 p-6 border border-gray-700 shadow-lg">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold">Note for the Chef</h3>
                <button
                  onClick={() => setIsCommentModalOpen(false)}
                  className="text-gray-400 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 rounded"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <textarea
                value={chefComment}
                onChange={(e) => setChefComment(e.target.value)}
                placeholder="Any special requests or preferences?"
                className="w-full h-32 p-3 bg-gray-800 border border-gray-600 rounded-lg text-gray-200 focus:outline-none focus:ring-2 focus:ring-red-500"
              />
              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setIsCommentModalOpen(false)}
                  className="px-6 py-2 border border-gray-600 text-gray-200 hover:bg-gray-800 rounded-lg transition-colors focus:ring-2 focus:ring-red-500"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (chefComment.trim()) {
                      setIsCommentModalOpen(false);
                    }
                  }}
                  className="flex items-center gap-2 px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg border border-red-500 transition-colors focus:ring-2 focus:ring-red-500"
                >
                  <Check className="w-4 h-4" />
                  <span>Save Note</span>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="flex justify-center mt-6">
        <a
          href="/menu/grills"
          className="px-6 py-2 text-sm bg-red-500 hover:bg-red-600 text-white font-bold rounded-full transition-colors"
        >
          See Our Full Menu
        </a>
      </div>
    </section>
  );
};

export default OurBestSellers;
